import { getRuntimeConfigList, getRuntimeConfig } from '@/modules/datasource-api/collectionAPI';
import { DBSource } from '@/modules/datasource-api/DBSource';
import { HTTPSource } from '@/modules/datasource-api/HTTPSource';
import { isDBLikeDataSource } from '@/modules/datasource-api/utils/tools';
import commonTSDeclaration from '!!raw-loader!../../datasource-api/types/DBSource.d.ts';
import { getValidPascalCase } from '@tencent/data-schema-core/cjs/convertor/typeOptions.js';
import { DATA_SOURCE_TYPE_PREFIX } from '@/modules/datasource-api/utils/const';
export async function getTypeDefinitions(projectId) {
    const allDataSources = await getRuntimeConfigList({
        projectId,
    });
    let hasDBDataSource = false;
    const typePrefix = DATA_SOURCE_TYPE_PREFIX;
    const dts = `declare module "@wujibase/wuji-data" {
${allDataSources.map(source => `  export const ${source.collectionId}: ${getValidPascalCase(`${typePrefix}-${source.collectionId}`)};`).join('\n')}
  export type { ${allDataSources.map(source => getValidPascalCase(`${typePrefix}-${source.collectionId}`)).join(', ')} }
}
${await Promise.all(allDataSources.map(async (source) => {
        const { collectionId } = source;
        let dataSource;
        if (isDBLikeDataSource(source)) {
            hasDBDataSource = true;
            dataSource = new DBSource({
                collectionId,
                projectId,
            });
        }
        else {
            dataSource = new HTTPSource({
                collectionId,
                projectId,
            });
        }
        return await dataSource.getTSDeclaration({ source, withCommon: false });
    })).then(results => results.join('\n'))}
  ${hasDBDataSource ? commonTSDeclaration : ''}
`;
    return dts;
}
/**
 * 数据源有以下几种引入方式：
 * 1. import { someSource } from '@wujibase/wuji-data';
 *
 * => import_wuji_data = require("@wujibase/wuji-data");
 *
 * 2. import wujiData from '@wujibase/wuji-data';
 *    const { someSource } = wujiData;
 *
 * => wujiData = __toESM(require("@wujibase/wuji-data"))
 *    const someSource = wujiData.default.someSource;
 *
 * 3. import * as wujiData from '@wujibase/wuji-data';
 *    const { someSource } = wujiData;
 *
 * => wujiData = __toESM(require("@wujibase/wuji-data"))
 *    const someSource = wujiData.someSource;
 *
 * 第二种和第三种方式最终会被 __toESM 转换
 *
 * __toESM 的工作原理：
 * var __toESM = (mod, isNodeMode, target) => (
 *   target = mod != null ? __create(__getProtoOf(mod)) : {},
 *   __copyProps(
 *     isNodeMode || !mod || !mod.__esModule
 *       ? __defProp(target, "default", { value: mod, enumerable: true })
 *       : target,
 *     mod
 *   )
 * );
 *
 * 关键点：
 * 1. __create(__getProtoOf(mod)) 会获取 mod 的原型并创建一个以此为原型的新对象
 * 2. __copyProps 只复制 mod 的自有可枚举属性（通过 Object.getOwnPropertyNames）
 * 3. 由于 Proxy 的 ownKeys() 初始只返回 ['__esModule', 'default']，
 *    数据源属性（如 someSource）不会被复制到 target
 *
 * 解决方案：
 * 1. 实现 getPrototypeOf trap 返回一个也具有代理行为的 protoProxy
 * 2. 这样 __toESM 创建的 target 对象会继承 protoProxy
 * 3. 当访问 target.someSource 时，会沿原型链查找到 protoProxy，触发代理逻辑
 */
export function createWujiData(projectId) {
    // 缓存已加载的数据源的方法
    const cache = {};
    // 缓存加载中的 Promise，避免重复请求
    const loadingPromises = {};
    // 为指定的数据源加载方法
    const loadMethods = async (collectionId) => {
        if (collectionId in cache)
            return cache[collectionId];
        if (collectionId in loadingPromises)
            return await loadingPromises[collectionId];
        const loadPromise = (async () => {
            const source = await getRuntimeConfig({
                collectionId,
                projectId,
            });
            if (!source) {
                throw new Error(`Datasource \`${collectionId}\` not found. Please check if the datasource was published when releasing the version.`);
            }
            let dataSource;
            if (isDBLikeDataSource(source)) {
                dataSource = new DBSource({
                    collectionId,
                    projectId,
                });
            }
            else {
                dataSource = new HTTPSource({
                    collectionId,
                    projectId,
                });
            }
            const methods = await dataSource.getMethods({ source });
            cache[collectionId] = methods;
            delete loadingPromises[collectionId];
            return methods;
        })();
        loadingPromises[collectionId] = loadPromise;
        return await loadPromise;
    };
    // 创建数据源方法的内部代理
    const createMethodProxy = (collectionId) => new Proxy({}, {
        get(methodTarget, methodName) {
            // 特殊处理 'then' 方法，防止代理对象被当作 thenable 处理
            // 这修复了用户使用 const source = await someSource; 时的问题
            // 如果不处理，JavaScript 会尝试调用 source.then() 导致报错
            if (methodName === 'then') {
                return undefined;
            }
            // 返回一个在首次调用时加载方法的函数
            return async (...args) => {
                const methods = await loadMethods(collectionId);
                const method = methods[methodName];
                if (typeof method !== 'function') {
                    throw new Error(`Method ${methodName} not found in datasource ${collectionId}`);
                }
                return await method(...args);
            };
        },
        has(methodTarget, methodName) {
            // 如果已加载，检查方法是否存在
            if (cache[collectionId]) {
                return methodName in cache[collectionId];
            }
            // 否则假设可能存在
            return true;
        },
        ownKeys() {
            // 如果已加载，返回实际的键
            if (cache[collectionId]) {
                return Object.keys(cache[collectionId]);
            }
            // 否则返回空数组
            return [];
        },
    });
    // 通用的 get 处理逻辑
    const getHandler = (target, collectionId) => {
        // 处理特殊属性
        if (collectionId === '__esModule') {
            return true;
        }
        if (collectionId === 'default') {
            // 返回自身作为 default
            return proxyObject;
        }
        // 处理 symbol 类型的属性
        if (typeof collectionId === 'symbol') {
            return undefined;
        }
        // 特殊处理 'then' 方法，防止代理对象被当作 thenable 处理
        // 这样 await wujiData 或 await wujiData.default 会直接返回对象本身
        if (collectionId === 'then') {
            return undefined;
        }
        return createMethodProxy(collectionId);
    };
    // 创建一个具有代理行为的自定义原型
    // 确保 __toESM 的 __create(__getProtoOf(mod)) 仍然能正常工作
    const protoProxy = new Proxy({}, {
        get(target, prop) {
            return getHandler(target, prop);
        },
    });
    // 创建 Proxy 实现数据源的懒加载和同步访问
    const proxyObject = new Proxy(Object.create(protoProxy), {
        get(target, collectionId) {
            return getHandler(target, collectionId);
        },
        has(target, prop) {
            // 特殊属性总是返回 true
            if (prop === '__esModule' || prop === 'default') {
                return true;
            }
            // 如果已缓存，直接返回 true
            // 否则无法确定（需要异步查询），返回 true 避免阻塞
            // 注意：这可能返回 false positive，但符合懒加载的设计
            return true;
        },
        ownKeys() {
            // 返回已缓存的 collectionId 以及特殊属性
            return ['__esModule', 'default', ...Object.keys(cache)];
        },
        getOwnPropertyDescriptor() {
            // 始终返回描述符以允许访问
            return {
                enumerable: true,
                configurable: true,
            };
        },
        getPrototypeOf() {
            // 返回 protoProxy，使 __toESM 的 __create(__getProtoOf(mod))
            // 创建的对象能继承代理行为
            return protoProxy;
        },
    });
    return proxyObject;
}
